// src/oura/primitives/MiniBars.tsx
//
// A handful of readings, as bars (#3831 follow-up).
//
// ONE DEFINITION for a few readings drawn as bars. A line between three points
// says the number moved continuously between them, and the space between two
// readings is unmeasured. Bars say "these are the readings" and nothing more.
//
// HTML rather than SVG. A viewBox scaled into a short wide box either
// letterboxes (the first cut clustered the bars in the middle of the card) or
// stretches, which distorts the value labels.

import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export function MiniBars({
    points,
    labels,
    showValues = true,
    breakIndex,
    className,
}: {
    points: number[];
    /** One label under each bar (a month name), in the same order as `points`. */
    labels?: ReactNode[];
    /**
     * The value above each bar. Off when there are too many bars for a
     * number to fit over each one: the value then stays for screen readers
     * and on hover, and the caller draws a scale beside the chart.
     */
    showValues?: boolean;
    /** The first reading taken on a NEW question set (#3539): the rule is drawn
     *  BETWEEN the bars, where the unmeasured gap is. */
    breakIndex?: number | null;
    className?: string;
}) {
    // Bars grow from zero, never from the floor of their own range. A line
    // scaled to its min made 1 → 6 climb the full height of the card, which
    // reads as a transformation rather than five visits.
    const scale = Math.max(1, ...points);
    const value = (p: number) => p.toLocaleString("en-GB");
    return (
        // Twelve bars on a phone need the narrower gap to leave each one a width.
        <div className={cn("flex items-end", points.length > 6 ? "gap-1 sm:gap-2" : "gap-2", className)}>
            {points.map((p, i) => {
                const last = i === points.length - 1;
                const cut = breakIndex != null && breakIndex === i && i > 0;
                return (
                    <div
                        key={i}
                        title={showValues ? undefined : value(p)}
                        className={cn(
                            "flex h-full min-w-0 flex-1 flex-col items-center justify-end",
                            cut && "border-l border-dashed border-amber-500 pl-2",
                        )}
                    >
                        {/* The value in its own row, so the bars below are
                            proportional to each other rather than to whatever
                            height the label happened to leave. */}
                        {showValues
                            ? <span className="mb-1 text-[11px] leading-none tabular-nums text-gray-500">{value(p)}</span>
                            : <span className="sr-only">{value(p)}</span>}
                        <div className="flex w-full flex-1 items-end justify-center">
                            {/* A zero reading still gets a sliver, so the reading is
                                visibly there and not mistaken for one that never ran. */}
                            <div
                                className={cn(
                                    "w-full max-w-[36px] rounded-sm bg-blue-600",
                                    last ? "opacity-100" : "opacity-35",
                                )}
                                style={{ height: `${Math.max(3, (p / scale) * 100)}%` }}
                            />
                        </div>
                        {labels && (
                            <span className="mt-1 h-7 whitespace-nowrap text-center text-[11px] leading-[14px] text-gray-400">
                                {labels[i]}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
